import React, {useState} from 'react';
import {Link, useRouteMatch} from 'react-router-dom';
import {Button} from '@betareader/design-system';
import {useSelector} from '../../reduxHooks';
import moment from 'moment';
import {Trans, useTranslation} from 'react-i18next';

import ReadingGraph from './readingGraph';
import JoinRequestItem from './joinRequest/joinRequestsItem';
import Avatar from '../../components/avatar/avatar';
import InvitationForm from './invitationForm';
import ReaderSignupQuestionsItem from './readerSignupQuestionsItem';
import SocialProfileLinks from './components/socialProfileLinks';

type Props = {
  reader: any;
  isEditable: boolean;
  contentVersionOptions: {key: string; value: string; text: string}[];
  canAddMoreReaders: boolean;
  isSelectable: boolean;
  onDelete: (readerId: string) => void;
  onMessage: (readerId: string) => void;
  onGoToProfile: (readerId: string) => void;
  onToggleEnabled: (readerId: string) => void;
  onShowReaderProfile: (userId: string) => void;
  onSelectedChange: (readerId: string) => void;
  onCopyURLToClipBoard: (token: string) => void;
  onApproveJoinRequest: (reader: any, content?: string) => void;
  onDeclineJoinRequest: (readerId: string) => void;
  onSendInvitation: (readerId: string) => void;
  onUpdateReader: (book: any, readerId: string, data: any) => Promise<void>;
  onRateReader: (userId: string) => void;
  onToggleFavorite: (readerId: string, favorite: boolean) => void;
};

const muted = {color: 'var(--br-muted)', fontSize: '0.8em'};
const actionsStyle = {
  display: 'grid',
  gap: 'var(--br-space-1)',
  position: 'absolute' as const,
  right: 0,
  top: '100%',
  zIndex: 10,
  minWidth: 180,
  padding: 'var(--br-space-2)',
  background: 'var(--br-surface)',
  border: '1px solid var(--br-border)',
  borderRadius: 'var(--br-control-radius)'
};

const ReaderEntryTableRow: React.FC<Props> = ({
  reader,
  isEditable,
  contentVersionOptions,
  canAddMoreReaders,
  isSelectable,
  onDelete,
  onMessage,
  onGoToProfile,
  onToggleEnabled,
  onShowReaderProfile,
  onSelectedChange,
  onCopyURLToClipBoard,
  onApproveJoinRequest,
  onDeclineJoinRequest,
  onSendInvitation,
  onUpdateReader,
  onRateReader,
  onToggleFavorite
}) => {
  const {t} = useTranslation();
  const route = useRouteMatch();
  const currentBook = useSelector(state => state.book.currentBook);
  const updatingReader = useSelector(state => state.reader.updatingReader);
  const [showInvitationForm, setShowInvitationForm] = useState(false);

  const status = reader.invitation.status;
  const disabledAccess = !reader.enabled || status === 'ABANDONED';
  const hasAccount = Boolean(reader.user?._id);
  const canBeMessaged = hasAccount &&
    ['APPROVED', 'ACCEPTED', 'ABANDONED'].includes(status);
  const completedAt = Object.prototype.hasOwnProperty.call(reader, 'currentVersionCompletedAt')
    ? reader.currentVersionCompletedAt
    : reader.completedBookAt;
  const canRateReader = hasAccount && status === 'ACCEPTED' &&
    Boolean(reader.completedBookAt) && !reader.authorRating;
  const hasBeenRated = reader.authorRating !== null && reader.authorRating !== undefined;
  const hasReadingCells = ['APPROVED', 'ACCEPTED', 'ABANDONED'].includes(status);
  const profileName = reader.user?.displayName || reader.name || t('Profile');

  const statusDate = (label: string, value?: string) => value && (
    <span style={muted}><br />{label} {new Date(value).toLocaleDateString()}</span>
  );

  const statusDetails = () => {
    if (status === 'APPROVED') return statusDate('Approved on', reader.invitation.approvedAt);
    if (status === 'ACCEPTED') return statusDate('Accepted on', reader.invitation.acceptedAt);
    if (status === 'DECLINED') return statusDate('Declined on', reader.invitation.declinedAt);
    if (status === 'SENT') return statusDate('Invited on', reader.invitation.sentAt);
    if (status === 'ABANDONED') return statusDate('Abandoned on', reader.invitation.abandonedAt);
    return null;
  };

  return (
    <tr data-reader-id={reader._id}
      style={status === 'BOUNCED' ? {background: 'var(--br-error-surface)'} : undefined}>
      {isSelectable && (
        <td>
          <input type='checkbox' checked={Boolean(reader.selected)}
            aria-label={`${t('Select')} ${profileName}`}
            onChange={() => onSelectedChange(reader._id)} />
        </td>
      )}
      <td data-user-id={reader.user?._id}>
        <Avatar name={reader.user?.displayName} src={reader.user?.photoUrl}
          diameter={30} selected={reader.selected}
          onClick={hasAccount ? () => onShowReaderProfile(reader.user._id) : undefined} />
        {reader.swap && (
          <details>
            <summary aria-label={t('SwapRequest')}>↔</summary>
            <div>
              <strong>{t('SwapRequest')}</strong>
              <p>
                <Trans i18nKey={['SENT', 'REQUESTED'].includes(status)
                  ? 'ThisIsASwapRequestForX' : 'ApprovedSwapRequestForX'}
                  values={{x: reader.swap.title || 'another book'}}>
                  <a href={`/discover/${reader.swap._id}`} target='betareader.io_reader'>
                    {reader.swap.title}
                  </a>
                </Trans>
              </p>
            </div>
          </details>
        )}
      </td>
      <td>
        {status !== 'REQUESTED' && !showInvitationForm &&
          `${reader.name || ''} ${reader.externalId ? `(${reader.externalId})` : ''}`}
        {!reader.enabled && status !== 'REQUESTED' && ' (access disabled)'}
        {statusDetails()}
        {status === 'CREATED' && showInvitationForm && (
          <InvitationForm invitationSent={() => setShowInvitationForm(false)}
            reader={reader} disabled={false} />
        )}
        {status === 'CREATED' && !showInvitationForm && (
          <span style={muted}>
            <br />⚠ {t('FailedToSendInvitation')}{' '}
            {updatingReader === reader._id ? 'Sending invitation' : (
              <Button variant='ghost' size='small'
                onClick={() => setShowInvitationForm(true)}>
                {t('EditInvitation').toLowerCase()}
              </Button>
            )}
          </span>
        )}
        {status === 'SENT' && updatingReader === reader._id && 'Sending invitation'}
        {status === 'REQUESTED' && <JoinRequestItem reader={reader} />}
        <ReaderSignupQuestionsItem questions={reader.readerSignupSurveyQuestions}
          initialOpen={status === 'REQUESTED'} />
      </td>
      <td>
        {reader.user?.socialProfiles && (
          <SocialProfileLinks socialProfiles={reader.user.socialProfiles}
            style={{fontSize: 30}} />
        )}
      </td>
      <td style={disabledAccess ? muted : undefined}>
        {moment(new Date(reader.createdAt)).fromNow()}
        <br /><span style={muted}>{moment(new Date(reader.createdAt)).format('L')}</span>
      </td>
      <td style={disabledAccess ? muted : undefined}>v{reader.contentVersionNumber || 1}</td>
      {hasReadingCells && (
        <td style={disabledAccess ? muted : undefined}>
          {status === 'APPROVED' ? 'Not started.' : (
            <ReadingGraph date={reader.readingData?.firstSeenAtDate}
              failureMessage='Not started' />
          )}
        </td>
      )}
      {hasReadingCells && (
        <td style={disabledAccess ? muted : undefined}>
          <Link to={`${route?.url || ''}/${reader._id}`}>
            {status === 'APPROVED' ? 'Not started.' : (
              <ReadingGraph date={reader.readingData?.lastSeenAtDate || completedAt}
                failureMessage='Not started' />
            )}
          </Link>
        </td>
      )}
      {hasReadingCells && (
        <td id={`last_seen_cell_${reader._id}`} style={disabledAccess ? muted : undefined}>
          {status === 'APPROVED' && 'Not started.'}
          {['ACCEPTED', 'ABANDONED'].includes(status) && (
            completedAt
              ? t('FinishedReadingAt', {x: new Date(completedAt).toLocaleDateString()})
              : reader.readingData?.lastSeenAtPartTitle || 'Not started.'
          )}
        </td>
      )}
      <td>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--br-space-1)'}}>
          {status === 'REQUESTED' && contentVersionOptions.length === 1 && (
            <Button variant='secondary' size='small'
              disabled={!isEditable || (reader.source !== 'book' && !canAddMoreReaders)}
              onClick={() => onApproveJoinRequest(reader)}>
              {reader.swap ? t('ApproveAndJoin') : t('Approve')}
            </Button>
          )}
          {status === 'REQUESTED' && contentVersionOptions.length > 1 && (
            <label className='br-field'>
              <span className='br-field__label'>{t('PickVersion')}</span>
              <select className='br-field__input' defaultValue=''
                disabled={!isEditable || (reader.source !== 'book' && !canAddMoreReaders)}
                onChange={event => {
                  if (event.target.value) onApproveJoinRequest(reader, event.target.value);
                }}>
                <option value='' disabled>{t('Accept')}</option>
                {contentVersionOptions.map(option => (
                  <option key={option.key} value={option.value}>{option.text}</option>
                ))}
              </select>
            </label>
          )}
          {status === 'REQUESTED' && (
            <Button variant='danger' size='small' disabled={!isEditable}
              onClick={() => onDeclineJoinRequest(reader._id)}>{t('Decline')}</Button>
          )}
          {canBeMessaged && (
            <Button variant='secondary' size='small' aria-label={t('Message')}
              onClick={() => onMessage(reader._id)}>{t('Message')}</Button>
          )}
          {isEditable && ['APPROVED', 'SENT', 'ACCEPTED', 'CREATED', 'ABANDONED'].includes(status) && (
            <details style={{position: 'relative'}}>
              <summary aria-label={t('MoreActions')} style={{cursor: 'pointer'}}>⋮</summary>
              <div style={actionsStyle}>
                <Button variant='ghost' size='small' disabled={!hasAccount}
                  onClick={() => onGoToProfile(reader.user._id)}>{t('Profile')}</Button>
                <Button variant='ghost' size='small' disabled={status === 'ACCEPTED'}
                  onClick={() => onDelete(reader._id)}>{t('Delete')}</Button>
                <Button variant='ghost' size='small'
                  disabled={reader.source !== 'book' && !canAddMoreReaders && !reader.enabled}
                  onClick={() => onToggleEnabled(reader._id)}>
                  {reader.enabled ? 'Disable' : 'Enable'}
                </Button>
                {currentBook?.content?.length > 1 && status !== 'ABANDONED' && (
                  <label className='br-field'>
                    <span className='br-field__label'>{t('SwitchVersion')}</span>
                    <select className='br-field__input' defaultValue=''
                      onChange={event => {
                        if (event.target.value) onUpdateReader(reader.book, reader._id, {
                          contentVersion: event.target.value
                        });
                      }}>
                      <option value='' disabled>{t('PickVersion')}</option>
                      {currentBook.content.map((version: any) => (
                        <option key={version._id} value={version._id}>
                          v{version.versionNumber}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {['CREATED', 'SENT'].includes(status) && (
                  <Button variant='ghost' size='small'
                    onClick={() => onCopyURLToClipBoard(reader.invitation.token)}>
                    Get invitation link
                  </Button>
                )}
                {status === 'SENT' && (
                  <Button variant='ghost' size='small'
                    onClick={() => onSendInvitation(reader._id)}>Resend invitation</Button>
                )}
                {(canRateReader || hasBeenRated) && (
                  <Button variant='ghost' size='small'
                    onClick={() => onRateReader(reader.user._id)}>
                    {hasBeenRated ? t('UpdateRating') : t('RateReader')}
                  </Button>
                )}
              </div>
            </details>
          )}
        </div>
      </td>
      <td>
        {hasAccount && (
          <Button variant='ghost' size='small' disabled={!isEditable}
            aria-label={reader.isFavorite ? t('RemoveFavoriteReader') : t('AddFavoriteReader')}
            aria-pressed={Boolean(reader.isFavorite)}
            onClick={() => onToggleFavorite(reader._id, !reader.isFavorite)}>
            <span aria-hidden='true'>{reader.isFavorite ? '★' : '☆'}</span>
          </Button>
        )}
      </td>
    </tr>
  );
};

export default ReaderEntryTableRow;
